// The in-meeting Zoom surface (TT-1207): the RTMS stream's transport (F2) and
// sharing the panel to the meeting (F3). The one place that names the SDK
// calls, so the components read as what they do, and the next least-privilege
// trim of the Marketplace listing can see what each capability is for
// (docs/zoom/ZOOM_APP_NOTES.md in the platform repo).
//
// The installed @zoom/appssdk (0.16.11) predates the RTMS methods, so they go
// through callZoomApi by name. An event is a plain addEventListener name: the
// SDK hands out whatever the client sends under it, so no SDK bump is needed
// to hear onRTMSStatusChange either.
import zoomSdk from "@zoom/appssdk";

import AppBackService from "./appback";

// What Zoom says about a stream, from getRTMSStatus and onRTMSStatusChange.
export const RTMS_STATUS = {
  CONNECTING: "connecting",
  STARTED: "started",
  PAUSED: "paused",
  STOPPED: "stopped",
};
// The event's word for a stream that was paused and is going again; to the
// bar that is started, so it never leaves this file.
const RESUMED = "resumed";

// The doc this meeting is streaming into, as the webhook's running doc
// (TT-1086): { docId, title, meeting } or null. `meeting` is what Zoom said
// about the meeting (topic, agenda, planned length), snake_case. A failed read
// is "none": the caller treats the meeting as not yet captured.
export async function readRunningMeetingDoc(userId) {
  const res = await readForMeeting(userId, AppBackService.getRunningFyre, "running-capture");
  return res ? { docId: res.docId, title: res.title, meeting: res.meeting || null } : null;
}

// This meeting's newest doc in any status (TT-1207 F1): { docId, title,
// status, setUp } or null. The meeting home opens on it: a running doc the
// user set up at once, an auto-started one nobody named after the naming, a
// closed one as the tapestry the meeting made.
export async function readMeetingDoc(userId) {
  const res = await readForMeeting(userId, AppBackService.getMeetingTapestry, "meeting-tapestry");
  return res ? { docId: res.docId, title: res.title, status: res.status, setUp: !!res.setUp } : null;
}

// The meeting's topic. Zoom gives it to the host; anyone else, and any
// failure, gets null.
export async function readMeetingTopic() {
  try {
    return (await zoomSdk.getMeetingContext())?.meetingTopic || null;
  } catch (error) {
    console.log("getMeetingContext unavailable:", error);
    return null;
  }
}

// One appback read keyed by this meeting's UUID; every failure is "none".
async function readForMeeting(userId, read, what) {
  if (!userId) return null;
  let meetingUuid = null;
  try {
    meetingUuid = (await zoomSdk.getMeetingUUID())?.meetingUUID || null;
  } catch (error) {
    console.log("getMeetingUUID failed:", error);
  }
  if (!meetingUuid) return null;
  try {
    const res = await read(userId, meetingUuid);
    return res && !res.error && res.docId ? res : null;
  } catch (error) {
    console.log(`${what} read failed; treating as none:`, error);
    return null;
  }
}

// Pause, resume and status need Zoom client 6.5.5; the event 6.5.10. An older
// client lists what it has; a failed read is "unknown", and the caller lets
// the call itself say no.
export async function supportedApis() {
  try {
    const res = await zoomSdk.getSupportedJsApis();
    return Array.isArray(res?.supportedApis) ? res.supportedApis : null;
  } catch (error) {
    console.log("getSupportedJsApis unavailable:", error);
    return null;
  }
}

export function supportsApi(list, name) {
  return list ? list.includes(name) : true;
}

// Zoom's status timestamps have been seen in both seconds and milliseconds
// across its surfaces; one clock for the bar.
export function timestampMs(ts) {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return null;
  return ts < 1e12 ? ts * 1000 : ts;
}

function normalizeSession(session) {
  if (!session) return null;
  return session.status === RESUMED ? { ...session, status: RTMS_STATUS.STARTED } : session;
}

// getRTMSStatus lists every session this app has in the meeting. Ours is the
// one still going; failing that, the latest, so a stopped stream still reads
// as stopped rather than as nothing.
export function currentSession(sessions) {
  const pool = Array.isArray(sessions) ? sessions.filter(Boolean) : [];
  const live = pool.filter((s) => s.status !== RTMS_STATUS.STOPPED);
  const pick = live.length ? live : pool;
  if (!pick.length) return null;
  const latest = pick.reduce((best, s) =>
    (timestampMs(s.timestamp) || 0) > (timestampMs(best.timestamp) || 0) ? s : best
  );
  return normalizeSession(latest);
}

export async function readRtmsStatus() {
  const res = await zoomSdk.callZoomApi("getRTMSStatus");
  return currentSession(res?.rtmsStatus);
}

export function startRtms() {
  return zoomSdk.callZoomApi("startRTMS");
}

export function pauseRtms() {
  return zoomSdk.callZoomApi("pauseRTMS");
}

export function resumeRtms() {
  return zoomSdk.callZoomApi("resumeRTMS");
}

// Zoom sends meeting.rtms_stopped for this; zoomevfn then closes the doc, the
// way a meeting's end does (TT-1198). So Stop here ends the tapestry.
export function stopRtms() {
  return zoomSdk.callZoomApi("stopRTMS");
}

// The handler gets the session as the bar reads it (resumed → started).
// Returns the unsubscribe, for an effect's cleanup.
export function onRtmsStatusChange(handler) {
  const listener = (event) => handler(normalizeSession(event));
  zoomSdk.addEventListener("onRTMSStatusChange", listener);
  return () => zoomSdk.removeEventListener("onRTMSStatusChange", listener);
}

// shareApp shows this panel to everyone in the meeting, view-only. Expanded
// first: shared from the sidebar it can go out at sidebar size (Zoom forum
// thread 102085). A client that cannot expand still shares.
export async function startShare() {
  try {
    await zoomSdk.expandApp({ action: "expand" });
  } catch (error) {
    console.log("expandApp unavailable; sharing at the current size:", error);
  }
  return zoomSdk.shareApp({ action: "start" });
}

// Stopping a share Zoom is not running answers 10025 ("Screen share did not
// start in this meeting"): the share had already ended, which is what a stop
// wants, so that resolves too.
const NOT_SHARING = 10025;
export async function stopShare() {
  try {
    return await zoomSdk.shareApp({ action: "stop" });
  } catch (error) {
    if (error?.code === NOT_SHARING) return { message: "already stopped" };
    throw error;
  }
}

// The handler gets "start" or "stop" (the SDK's OnShareAppEvent). It also
// fires when the user ends the share from Zoom's own controls, so it is what
// drives the toggle's state. The client posts the event as "shareApp" (under
// "onShareApp" alone nothing arrived); the newer SDK listens under both
// names, so clients differ, and so do we. Returns the unsubscribe.
const SHARE_EVENTS = ["shareApp", "onShareApp"];
export function onShareApp(handler) {
  SHARE_EVENTS.forEach((name) => zoomSdk.addEventListener(name, handler));
  return () => SHARE_EVENTS.forEach((name) => zoomSdk.removeEventListener(name, handler));
}

// Post a line to the meeting's chat (TT-1207 F4). Zoom posts it as the user;
// from the sidebar the client first asks which channel. Desktop 6.2.5+, any
// role. By name: the installed SDK predates it. A refusal comes back worded
// for the user, so the two share surfaces show one sentence.
export async function postToMeetingChat(message) {
  try {
    return await zoomSdk.callZoomApi("sendMessageToChat", { message });
  } catch (error) {
    console.log("sendMessageToChat failed:", error);
    throw new Error(`Could not post to the meeting chat: ${error?.message || error}`);
  }
}

// What the app posts: a Tapestry's title, then its image link …
export function postTapToChat(title, imageUrl) {
  return postToMeetingChat(title ? `${title}: ${imageUrl}` : imageUrl);
}

// … or an invitation to watch a live one.
export function postLiveLinkToChat(docTitle, url) {
  return postToMeetingChat(`Watch ${docTitle ? `“${docTitle}”` : "this Tapestry"} live: ${url}`);
}
